import React from 'react';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, Button, TextField, NumberField, SelectField, FlexRow, RotatingIcon, IconType } from 'ui-toolkit';
import GrayLine from 'components/shared/GrayLine';
import { useFormikContext } from 'formik';
import { IEscrowFileCompleteData } from 'types/order/escrow/IEscrowFileCompleteData';
import { EscrowWireDisbursementCategoriesOptions } from '../achPushDisbursements/createWireFromACH/EscrowWireDisbursementCategoriesOptions';
import { ePermissions } from '../../../../../types/administration/userAdministration/ePermissions';
import { useGetBankNameByRoutingNumberQuery } from 'API/order/escrow/EscrowBaseAPI';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

export interface ICreateEditWireDisbursementsRowProps {
  index: number;
  onDelete: (index: number) => void;
  fieldName: string;
  onSend: () => void;
  fetching: boolean;
  routingNumber?: string;
}

export const CreateEditWireDisbursementsRow: React.FC<ICreateEditWireDisbursementsRowProps> = ({
  index,
  onDelete,
  fieldName,
  onSend,
  fetching,
  routingNumber,
}) => {
  const hasEditPermission = useHasPermissions([ePermissions.EscrowExpectedInternalTransfersEdit]);
  const { data: bank } = useGetBankNameByRoutingNumberQuery(routingNumber ?? '', { skip: routingNumber?.length !== 9 });
  const { values } = useFormikContext<IEscrowFileCompleteData>();
  const wireDisbursementId = values.wireDisbursements && values.wireDisbursements[index].id;
  const isWirePersisted: boolean = wireDisbursementId !== undefined;

  return (
    <>
      <Grid>
        <FlexRow>
          <Grid templateColumns={'2fr 1fr 1fr 0.2fr'} alignItems={`center`} width={'100%'}>
            <TextField name={`${fieldName}.${index}.beneficiary`} isRequired label={'Beneficiary Name'} />
            <TextField
              name={`${fieldName}.${index}.routingNumber`}
              label="Routing Number"
              hintText={bank?.name}
              isRequired
            />
            <TextField name={`${fieldName}.${index}.accountNumber`} isRequired label={'Account Number'} />
            <Button
              intent={'ghost'}
              isInline
              onClick={() => onDelete(index)}
              tabIndex={-1}
              data-test-id={'remove-wire-disbursement'}
            >
              <FontAwesomeIcon icon={faClose} />
            </Button>
          </Grid>
        </FlexRow>
        <FlexRow>
          <Grid templateColumns={'2fr 1fr 1fr 0.2fr'} alignItems={`center`} width={'100%'}>
            <TextField name={`${fieldName}.${index}.memo`} label={'Memo/OBI'} maxCharacterCount={160} />
            <NumberField
              name={`${fieldName}.${index}.amountCents`}
              isRequired
              label={'Amount'}
              valueIsCents
              mode="currency"
            />
            <SelectField
              name={`${fieldName}.${index}.category`}
              options={EscrowWireDisbursementCategoriesOptions}
              label={'Category'}
              isRequired
            />
          </Grid>
        </FlexRow>

        {isWirePersisted && (
          <FlexRow justifyContent={'start'} verticalAlign={'center'}>
            {fetching && <RotatingIcon type={IconType.Loading} />}
            <Button
              intent={'primary'}
              type={'button'}
              onClick={onSend}
              disabled={!hasEditPermission || !isWirePersisted}
              data-test-id={'send-wire-disbursement'}
            >
              Send
            </Button>
          </FlexRow>
        )}
      </Grid>
      <GrayLine />
    </>
  );
};
